import { Injectable, inject } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { AppStateService } from '../services/app-state.service';
import { AuthOAuthService } from '../services/oauth.service';
import { UserRole } from '../models/user.model';
import { Observable } from 'rxjs';

/** Route metadata interface for RBAC */
interface RouteData {
  roles?: UserRole[];
}

/** Route guard for authentication and role-based authorization */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly appState = inject(AppStateService);
  private readonly oauth = inject(AuthOAuthService);

  /** Check if user is authenticated and has required role for route */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    // 1) Validate token with single check (prevents race conditions)
    const tokenValidation = this.isTokenValid();
    if (!tokenValidation.valid) {
      this.logSecurity('TOKEN_INVALID', {
        url: state.url,
        reason: tokenValidation.reason
      });

      // Token invalid or expired — redirect to login
      const returnUrl = this.sanitizeReturnUrl(state.url);
      return this.router.createUrlTree(['/login'], {
        queryParams: returnUrl ? { returnUrl } : {}
      });
    }

    // 2) Role-based authorization from route data
    const rolesData = (route.data as RouteData | undefined)?.roles;
    const allowedRoles = Array.isArray(rolesData)
      ? rolesData.filter(r => Object.values(UserRole).includes(r))
      : [];

    if (allowedRoles.length > 0) {
      const user = this.appState.currentUser();
      const userRole = user?.userType;

      // If no user but we have a valid token, wait a moment for app init to complete
      // This handles the case where guard runs before APP_INITIALIZER finishes
      if (!user && this.oauth.getAccessToken()) {
        return this.waitForUserInitialization(route, state, allowedRoles);
      }

      if (!userRole || !allowedRoles.includes(userRole)) {
        this.logSecurity('INSUFFICIENT_PERMISSIONS', {
          url: state.url,
          userRole: userRole || 'undefined',
          requiredRoles: allowedRoles,
          userId: user?.userId || 'unknown'
        });
        return this.router.createUrlTree(['/forbidden']);
      }
    }

    return true;
  }

  /** Wait for user initialization to complete if app init is still running */
  private waitForUserInitialization(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    allowedRoles: UserRole[]
  ): Promise<boolean | UrlTree> {
    // Wait up to 3 seconds for user to be initialized
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 30; // 30 * 100ms = 3 seconds

      const checkUser = setInterval(() => {
        attempts++;
        const user = this.appState.currentUser();
        const userRole = user?.userType;

        if (user && userRole && allowedRoles.includes(userRole)) {
          clearInterval(checkUser);
          resolve(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkUser);
          console.warn('[GUARD] User initialization timeout');
          this.logSecurity('AUTHORIZATION_TIMEOUT', {
            url: state.url,
            reason: 'User not initialized within timeout'
          });
          resolve(this.router.createUrlTree(['/login']));
        }
      }, 100);
    });
  }

  /** Single token validation to prevent race conditions */
  private isTokenValid(): { valid: boolean; reason?: string } {
    const accessToken = this.oauth.getAccessToken();

    if (!accessToken) {
      return { valid: false, reason: 'NO_TOKEN' };
    }

    if (this.oauth.isTokenExpired()) {
      return { valid: false, reason: 'TOKEN_EXPIRED' };
    }

    return { valid: true };
  }

  
  /** Validate URL is safe internal path to prevent open redirects */
  private sanitizeReturnUrl(url: string): string | null {
    if (!url?.trim() || !url.startsWith('/')) return null;

    // Block auth loops and root
    const authPrefixes = ['/login', '/logout', '/callback', '/forbidden'];
    if (url === '/' || authPrefixes.some(p => url === p || url.startsWith(p + '/'))) return null;

    // Disallow protocol-relative or absolute (e.g., //host or http://host)
    try {
      const test = new URL(url, globalThis.location.origin);
      if (test.origin !== globalThis.location.origin) return null; // escaped origin
    } catch { return null; }

    return url;
  }

  /** Log security events for audit trails and debugging */
  private logSecurity(event: string, details: Record<string, unknown>): void {
    // eslint-disable-next-line no-console
    console.warn('[SECURITY_EVENT]', {
      ts: new Date().toISOString(),
      user: this.appState.currentUser()?.email ?? 'anonymous',
      event,
      ...details
    });
  }
}
